import React from 'react';

import PromptBox from '../../PromptBox';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../ui/card';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { IntakeFormData } from '../form/types';

interface LegalLifestyleSectionProps {
  form: IntakeFormData;
  onFieldChange: (
    section: keyof IntakeFormData,
    field: string,
    value: any,
  ) => void;
}

const LegalLifestyleSection: React.FC<LegalLifestyleSectionProps> = ({
  form,
  onFieldChange,
}) => {
  const handleInputChange = (field: string, value: string) => {
    onFieldChange('legalLifestyle', field, value);
  };

  return (
    <div className="space-y-3">
      <PromptBox
        title="Suggested Questions"
        suggestions={[
          'Have you been involved in any previous lawsuits?',
          'How has this incident affected your daily life?',
          'What activities can you no longer do due to your injuries?',
          'Have you missed work due to your injuries?',
          'Have you incurred any out-of-pocket expenses?',
        ]}
      />

      {/* Prior Legal History */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"
              />
            </svg>
            <CardTitle className="text-base">Prior Legal History</CardTitle>
          </div>
          <CardDescription className="text-xs text-gray-500 leading-tight">
            Previous legal matters and attorney relationships
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-1">
            <Label htmlFor="priorLegalHistory" className="text-sm font-medium">
              Prior Legal History Notes
            </Label>
            <Textarea
              id="priorLegalHistory"
              value={form.legalLifestyle.priorLegalHistory}
              onChange={(e) =>
                handleInputChange('priorLegalHistory', e.target.value)
              }
              placeholder="Any previous lawsuits, claims, or attorney relationships. Include dates and outcomes if known."
              rows={4}
              aria-label="Prior legal history"
              tabIndex={0}
              className="text-sm"
            />
          </div>
        </CardContent>
      </Card>

      {/* Lifestyle Impact */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Lifestyle Impact</CardTitle>
          <CardDescription className="text-xs text-gray-500 leading-tight">
            How the incident has affected daily activities and quality of life
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-1">
            <Label htmlFor="lifestyleImpact" className="text-sm font-medium">
              Lifestyle Impact Description
            </Label>
            <Textarea
              id="lifestyleImpact"
              value={form.legalLifestyle.lifestyleImpact}
              onChange={(e) =>
                handleInputChange('lifestyleImpact', e.target.value)
              }
              placeholder="Describe how this incident has affected your daily life, activities you can no longer do, and quality of life changes..."
              rows={4}
              aria-label="Lifestyle impact"
              tabIndex={0}
              className="text-sm"
            />
          </div>
        </CardContent>
      </Card>

      {/* Financial Impact */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Financial Impact</CardTitle>
          <CardDescription className="text-xs text-gray-500 leading-tight">
            Out-of-pocket expenses and pending bills
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="pendingBills" className="text-sm font-medium">
                Pending Bills
              </Label>
              <Textarea
                id="pendingBills"
                value={form.legalLifestyle.pendingBills}
                onChange={(e) =>
                  handleInputChange('pendingBills', e.target.value)
                }
                placeholder="List any medical bills, property damage, or other expenses related to the incident..."
                rows={4}
                aria-label="Pending bills"
                tabIndex={0}
                className="text-sm"
              />
            </div>

            <div className="space-y-1">
              <Label
                htmlFor="outOfPocketExpenses"
                className="text-sm font-medium"
              >
                Out of Pocket Expenses
              </Label>
              <Textarea
                id="outOfPocketExpenses"
                value={form.legalLifestyle.outOfPocketExpenses}
                onChange={(e) =>
                  handleInputChange('outOfPocketExpenses', e.target.value)
                }
                placeholder="Any expenses you've already paid out of pocket..."
                rows={4}
                aria-label="Out of pocket expenses"
                tabIndex={0}
                className="text-sm"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Additional Information */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Additional Information</CardTitle>
          <CardDescription className="text-xs text-gray-500 leading-tight">
            Any other relevant information or notes
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-1">
            <Label htmlFor="otherNotes" className="text-sm font-medium">
              Other Notes
            </Label>
            <Textarea
              id="otherNotes"
              value={form.legalLifestyle.otherNotes}
              onChange={(e) => handleInputChange('otherNotes', e.target.value)}
              placeholder="Any additional information that might be relevant to your case..."
              rows={4}
              aria-label="Other notes"
              tabIndex={0}
              className="text-sm"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LegalLifestyleSection;
